import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, ButtonGroup, CircularProgress, Divider,
  LinearProgress, Alert, Switch, FormControlLabel, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, useTheme,
} from '@mui/material';
import {
  InsightsOutlined, RecordVoiceOverOutlined, ForumOutlined,
  PersonOutlineOutlined, MemoryOutlined, CalendarTodayOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import http from '../../services/http';

/**
 * De onde vem o gasto de IA (ponto 16, parte 2).
 *
 * A parte 1 passou a REGISTRAR o consumo -- `ai_usage_logs` existia desde maio
 * com zero linhas e nenhum chamador. Aqui ele fica visível.
 *
 * A leitura mais importante é POR ORIGEM: as medições diziam que a transcrição
 * da URA é 99% do gasto de OpenAI, e essa parte era cega. Por isso ela vem
 * primeiro na tela, e ordenada por consumo.
 *
 * Não mostra reais de propósito — ver o rodapé, que explica. Estimar exigiria
 * uma tabela de preços por modelo chumbada no código mais uma cotação do
 * dólar; as duas envelhecem, e o resultado seria um número errado apresentado
 * como fato.
 */

/** Os nomes técnicos do `endpoint` não vão crus para a tela. */
const NOME_DA_ORIGEM = {
  'ura.transcricao': 'Transcrição da URA',
  'chat.transcricao': 'Transcrição de áudio do chat',
  'orby.chat': 'Chat do Orby',
};

const ICONE_DA_ORIGEM = {
  'ura.transcricao': <RecordVoiceOverOutlined sx={{ fontSize: 18 }} />,
  'chat.transcricao': <RecordVoiceOverOutlined sx={{ fontSize: 18 }} />,
  'orby.chat': <ForumOutlined sx={{ fontSize: 18 }} />,
};

const PERIODOS = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
];

const numero = (valor) => new Intl.NumberFormat('pt-BR').format(Number(valor) || 0);

const diaCurto = (iso) => {
  const partes = String(iso || '').split('-');
  if (partes.length !== 3) return String(iso || '');
  return `${partes[2]}/${partes[1]}`;
};

/** Um card de total. */
const Total = ({ label, valor, icone }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 180px',
        minWidth: 160,
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.text.secondary }}>
        {icone}
        <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>{label}</Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>{valor}</Typography>
    </Paper>
  );
};

/**
 * Uma linha com barra proporcional ao maior valor da lista.
 *
 * A barra é relativa ao MAIOR da própria lista, não ao total: é o que faz a
 * desproporção entre a URA e o resto aparecer de relance, que é o ponto da
 * tela.
 */
const Linha = ({ rotulo, icone, tokens, registros, maximo }) => {
  const theme = useTheme();
  const proporcao = maximo > 0 ? Math.round((tokens / maximo) * 100) : 0;

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icone && <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>{icone}</Box>}
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{rotulo}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{numero(tokens)}</Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: 72, textAlign: 'right' }}>
          {numero(registros)} {Number(registros) === 1 ? 'registro' : 'registros'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={proporcao}
        sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.action.hover }}
      />
    </Box>
  );
};

const Secao = ({ titulo, icone, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: theme.palette.text.secondary }}>
        {icone}
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{titulo}</Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      {children}
    </Paper>
  );
};

const Vazio = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 1 }}>
      {children}
    </Typography>
  );
};

const maiorToken = (lista) => (lista || []).reduce((max, l) => Math.max(max, Number(l.tokens) || 0), 0);

/**
 * O switch da transcrição da URA.
 *
 * Mora nesta tela, e não numa de configurações, porque é aqui que o gasto
 * aparece: ver o número e a chave que o inicia no mesmo lugar.
 *
 * **Ligar pede confirmação; desligar não.** A assimetria é deliberada —
 * desligar só para de gastar, ligar começa.
 *
 * **Quem não pode ligar não vê a chave.** Mostrar um switch que devolve 403
 * faria a pessoa concluir que está quebrado, não que é proibido.
 */
const SwitchDaTranscricao = ({ estado, onMudou }) => {
  const theme = useTheme();
  const [confirmando, setConfirmando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const ativa = Boolean(estado?.ativa);
  const pendentes = Number(estado?.pendentes) || 0;
  const janela = Number(estado?.janela_horas) || 6;

  const aplicar = async (novoValor) => {
    setSalvando(true);
    try {
      await http.put('/ai-usage/transcricao-da-ura', { ativa: novoValor });
      toast.success(novoValor ? 'Transcrição da URA ligada.' : 'Transcrição da URA desligada.');
      if (onMudou) onMudou();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Não foi possível alterar a transcrição da URA.');
    } finally {
      setSalvando(false);
      setConfirmando(false);
    }
  };

  const aoClicar = () => {
    if (ativa) {
      aplicar(false);
      return;
    }
    setConfirmando(true);
  };

  if (!estado?.pode_ligar) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper }}
      >
        <FormControlLabel
          control={<Switch checked={ativa} onChange={aoClicar} disabled={salvando} />}
          label={
            <Box>
              {/* "Transcrever gravações da URA", e não "Transcrição da URA":
                  o switch é uma AÇÃO, e o segundo texto é o nome da linha de
                  consumo logo abaixo -- os dois iguais na mesma tela fariam o
                  leitor achar que a chave liga aquela linha específica. */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Transcrever gravações da URA
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {ativa
                  ? 'Ligada: as gravações estão sendo transcritas e isso consome a cota da OpenAI.'
                  : 'Desligada: nenhuma gravação está sendo transcrita, e nada está sendo gasto.'}
              </Typography>
            </Box>
          }
        />
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.text.secondary }}>
          {pendentes} gravação(ões) aguardando nas últimas {janela} horas. Ligar processa
          essa janela, e não o atraso antigo — o comando olha só as últimas {janela} horas.
        </Typography>
      </Paper>

      <Dialog open={confirmando} onClose={() => setConfirmando(false)}>
        <DialogTitle>Ligar a transcrição da URA?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A partir de agora as gravações de ligação passam a ser transcritas pela OpenAI,
            e isso consome a sua cota. Há {pendentes} gravação(ões) aguardando nas últimas{' '}
            {janela} horas, que serão processadas nos próximos minutos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => aplicar(true)} disabled={salvando}>
            Ligar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ConsumoDeIA = () => {
  const theme = useTheme();
  const [dias, setDias] = useState(30);
  const [relatorio, setRelatorio] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(async (periodo) => {
    setCarregando(true);
    setErro(false);
    try {
      const res = await http.get('/ai-usage/stats', { params: { dias: periodo } });
      setRelatorio(res.data || null);
    } catch (e) {
      setErro(true);
      setRelatorio(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(dias); }, [dias, carregar]);

  if (erro) {
    return <Alert severity="error">Não foi possível carregar o consumo de IA.</Alert>;
  }

  if (carregando && !relatorio) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const total = relatorio?.total || { registros: 0, tokens: 0 };
  const porOrigem = relatorio?.por_origem || [];
  const porModelo = relatorio?.por_modelo || [];
  const porDia = relatorio?.por_dia || [];
  const porColaborador = relatorio?.por_colaborador || [];
  const semConsumo = Number(total.registros) === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Consumo de IA</Typography>
        <ButtonGroup size="small">
          {PERIODOS.map((p) => (
            <Button
              key={p.dias}
              variant={dias === p.dias ? 'contained' : 'outlined'}
              onClick={() => setDias(p.dias)}
            >
              {p.rotulo}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Total label="TOKENS NO PERÍODO" valor={numero(total.tokens)} icone={<InsightsOutlined sx={{ fontSize: 18 }} />} />
        <Total label="CHAMADAS À IA" valor={numero(total.registros)} icone={<MemoryOutlined sx={{ fontSize: 18 }} />} />
      </Box>

      {/* Antes dos agrupamentos, de propósito: a chave que inicia o gasto vem
          junto do número que ele produz. */}
      <SwitchDaTranscricao
        estado={relatorio?.transcricao_da_ura}
        onMudou={() => carregar(dias)}
      />

      {semConsumo && (
        <Alert severity="info">
          Nenhum consumo registrado neste período. O registro de consumo é recente — se a
          versão em produção ainda não o inclui, esta tela permanece vazia até o próximo deploy.
        </Alert>
      )}

      {!semConsumo && (
        <>
          <Secao titulo="Por origem" icone={<InsightsOutlined sx={{ fontSize: 18 }} />}>
            {porOrigem.map((o) => (
              <Linha
                key={o.endpoint}
                rotulo={NOME_DA_ORIGEM[o.endpoint] || o.endpoint}
                icone={ICONE_DA_ORIGEM[o.endpoint]}
                tokens={o.tokens}
                registros={o.registros}
                maximo={maiorToken(porOrigem)}
              />
            ))}
          </Secao>

          <Secao titulo="Quem usou o Orby" icone={<PersonOutlineOutlined sx={{ fontSize: 18 }} />}>
            {porColaborador.length === 0 ? (
              <Vazio>
                Ninguém usou o Orby neste período. A transcrição da URA não aparece
                aqui porque nasce de uma ligação, sem pessoa por trás.
              </Vazio>
            ) : porColaborador.map((c) => (
              <Linha
                key={c.user_id}
                rotulo={c.nome || `Operador #${c.user_id}`}
                tokens={c.tokens}
                registros={c.registros}
                maximo={maiorToken(porColaborador)}
              />
            ))}
          </Secao>

          <Secao titulo="Por modelo" icone={<MemoryOutlined sx={{ fontSize: 18 }} />}>
            {porModelo.length === 0 ? <Vazio>Sem dados.</Vazio> : porModelo.map((m) => (
              <Linha
                key={m.model || 'sem-modelo'}
                rotulo={m.model || 'Não informado'}
                tokens={m.tokens}
                registros={m.registros}
                maximo={maiorToken(porModelo)}
              />
            ))}
          </Secao>

          <Secao titulo="Por dia" icone={<CalendarTodayOutlined sx={{ fontSize: 18 }} />}>
            {porDia.length === 0 ? <Vazio>Sem dados.</Vazio> : porDia.map((d) => (
              <Linha
                key={d.dia}
                rotulo={diaCurto(d.dia)}
                tokens={d.tokens}
                registros={d.registros}
                maximo={maiorToken(porDia)}
              />
            ))}
          </Secao>
        </>
      )}

      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        Os números são de tokens e chamadas, e a tela não converte em reais: o preço depende
        da tabela de cada modelo e da cotação do dólar, e um valor estimado aqui envelheceria
        sem avisar. O modelo é o que diz como a cobrança foi feita — os modelos de chat e o
        gpt-4o-mini-transcribe cobram por token; o whisper-1 cobra por minuto de áudio, e por
        isso aparece com zero token.
      </Typography>
    </Box>
  );
};

export default ConsumoDeIA;
